import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError, combineLatest } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	mapDeviceAuthenticationFromSysApi,
	DeviceAuthenticationStatusService
} from '@shure/shared/angular/data-access/device-authentication';
import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	ApolloQueryError,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { mapDeviceModelFromSysApi, mapValueOrDefault } from '@shure/shared/angular/data-access/system-api/mappers';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { AuthenticationDevice, DeviceAuthenticationApiService } from '../api';

import {
	DeviceAuthenticationDeviceFragment,
	DeviceSubscriptionGQL,
	GetDevicesQueryGQL
} from './graphql/generated/device-authentication.generated';

@Injectable({ providedIn: 'root' })
export class SysApiDeviceAuthenticationApiService implements DeviceAuthenticationApiService {
	private readonly logger: ILogger;

	private readonly devicesGqlSubscriptions: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly getDevicesQueryGql: GetDevicesQueryGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		deviceSubscriptionGql: DeviceSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('SysApiDeviceAuthenticationApiService');

		this.devicesGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiDeviceAuthenticationApiService',
				changeTypes: [NodeChangeType.DeviceName, NodeChangeType.DeviceAuthentication],
				subscriptionGql: deviceSubscriptionGql
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevices$(deviceIds: string[]): Observable<AuthenticationDevice[]> {
		const queryId = uuidv4();
		this.logger.trace('getDevices$()', 'Getting devices', { deviceIds, queryId });

		return combineLatest([
			this.getDevicesQueryGql
				.watch({ variables: { ids: deviceIds }, errorPolicy: 'all' })
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			combineLatest(deviceIds.map((id) => this.deviceAuthenticationStatusService.getDeviceState$(id)))
		]).pipe(
			map(([query, auths]) => {
				if (!query.data?.nodes) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const devices: AuthenticationDevice[] = [];
				for (const node of query.data.nodes) {
					if (node !== null && 'isDeviceNode' in node) {
						const auth = auths.find((f) => f.id === node.id);
						devices.push(
							mapDeviceAuthenticationDeviceFromSysApi(
								node,
								auth?.authenticated || false,
								auth?.readonlyMode || false
							)
						);
					}
				}

				this.logger.trace('getDevices$()', 'Query updated', { devices });
				return devices;
			}),
			tapOnce((devices) =>
				this.devicesGqlSubscriptions.subscribeToOnly(
					queryId,
					devices.map((d) => d.id)
				)
			),
			noMoreSubscribers(() => {
				this.logger.trace('getDevices$()', 'noMoreSubscribers', {
					deviceIds,
					queryId
				});
				this.devicesGqlSubscriptions.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevices$()', 'Failed to query devices', {
					deviceIds,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}
}

function mapDeviceAuthenticationDeviceFromSysApi(
	fragment: DeviceAuthenticationDeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): AuthenticationDevice {
	const {
		id,
		virtual,
		description: { model },
		features: { name, authentication, controlNetwork }
	} = fragment;

	return {
		id,
		virtual: virtual,
		model: mapDeviceModelFromSysApi(model),
		name: mapValueOrDefault(name?.name),
		features: {
			authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected),
			controlNetwork: { ipAddress: controlNetwork?.interface?.ipAddress ?? '', isMissing: !controlNetwork }
		}
	};
}
